/**
 * LE NOM D'UNE PIÈCE JOINTE D'ÉQUIPEMENT — copie d'écran de la règle serveur.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * ⚠️ LA RÈGLE FAIT FOI CÔTÉ SERVEUR (`backend/src/pieces-nom.ts`). Celle-ci
 * n'existe que pour MONTRER, avant de verser, le nom que la pièce portera : un
 * nom qu'on découvre après coup est un nom qu'on ne discute plus.
 *
 * Les deux ne peuvent pas être un seul fichier — le socle `shared/ui/` n'est
 * recopié que dans les trois applications Next, jamais dans le backend, et le
 * backend est un projet TypeScript à part. Elles sont donc DEUX, et c'est une
 * duplication assumée à une condition : la suite `pieces-nom-parite` compile
 * les deux modules et les confronte sur une batterie de cas. Modifier l'un sans
 * l'autre fait échouer `npm test`, immédiatement et en nommant le cas.
 *
 * Ne rien ajouter ici sans l'ajouter là-bas.
 */

/** Translittération sans dépendance : NFD sépare la lettre de son accent, la
 *  plage combinante s'enlève ensuite d'un coup. « Étalonnage » devient
 *  « Etalonnage » plutôt que « talonnage ». `_` est CONSERVÉ bien qu'il serve
 *  de séparateur : les codes du parc en portent (`MIC_04`, `MP_Bact1`). */
export function assainir(brut: string): string {
  return String(brut ?? '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^A-Za-z0-9._-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^[-.]+|[-.]+$/g, '')
    .slice(0, 60);
}

export interface PieceANommer {
  nature: string;
  reference?: string;
  version?: string;
  /** AAAA-MM-JJ, ou vide : aucune date n'est inventée ici. */
  date: string;
  ext: string;
}

/** `CODE_nature[_référence][_vVERSION]_AAAA-MM-JJ.ext` */
export function nomPiece(code: string, p: PieceANommer, pris: Iterable<string> = []): string {
  const morceaux = [assainir(code) || 'equipement', assainir(p.nature) || 'autre'];
  const ref = assainir(p.reference || '');
  if (ref) morceaux.push(ref);
  const ver = assainir(p.version || '');
  if (ver) morceaux.push(/^v/i.test(ver) ? ver : `v${ver}`);
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(p.date || ''))) morceaux.push(p.date);
  const ext = assainir(p.ext || '').toLowerCase() || 'bin';

  const base = morceaux.join('_');
  const dejaLa = new Set(Array.from(pris, (x) => String(x).toLowerCase()));
  let nom = `${base}.${ext}`;
  for (let n = 2; dejaLa.has(nom.toLowerCase()); n += 1) nom = `${base}_${n}.${ext}`;
  return nom;
}

/** L'extension telle que le serveur la retiendra. Il la déduit de la SIGNATURE
 *  binaire, pas du nom : l'aperçu s'en approche par le nom, faute de pouvoir
 *  lire les octets — c'est le seul endroit où les deux règles diffèrent, et il
 *  ne porte que sur l'aperçu. `jpeg` est ramené à `jpg`, comme côté serveur. */
export function extensionApercu(nomFichier: string): string {
  const brut = (/\.([A-Za-z0-9]+)$/.exec(String(nomFichier || '')) || [])[1] || '';
  const ext = brut.toLowerCase();
  return ext === 'jpeg' ? 'jpg' : ext;
}
